import { useEffect, useState } from "react";
import {
  Divider,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { Box, Card, CardContent, Typography } from "../../../../components";
import { OrdemServicoService } from "../../../../services";
import { formatPrice, responseErros } from "../../../../utils";

const ordemServicoService = new OrdemServicoService();

const CORES_RANK = ["#f59e0b", "#94a3b8", "#b45309"];

const defaultData = {
  clientes: [],
  totalClientes: 0,
  totalFaturamento: 0,
  percentualClientes: 0,
};

function BarraProgresso({ valor, max, cor, label, labelWidth = 40 }) {
  const pct = max > 0 ? (valor / max) * 100 : 0;
  return (
    <Box display="flex" alignItems="center" gap={1}>
      <Box
        sx={{
          flex: 1,
          height: 8,
          borderRadius: 4,
          backgroundColor: "#f1f5f9",
          overflow: "hidden",
          minWidth: 48,
        }}
      >
        <Box
          sx={{
            width: `${pct}%`,
            height: "100%",
            borderRadius: 4,
            backgroundColor: cor,
            transition: "width 0.3s",
          }}
        />
      </Box>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "#1e293b",
          minWidth: labelWidth,
          textAlign: "right",
          whiteSpace: "nowrap",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

function ResumoTotais({
  quantidade,
  totalClientes,
  totalFaturamento,
  percentual,
}) {
  return (
    <Box
      sx={{
        mt: "auto",
        p: 1.5,
        borderRadius: 2,
        backgroundColor: "#f8fafc",
        border: "1px solid #eef2f7",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Total dos {quantidade} maiores clientes
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#1e293b" }}>
          {formatPrice(totalClientes)}
        </Typography>
      </Box>
      <Box textAlign="right">
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          Faturamento do período: {formatPrice(totalFaturamento)}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: 18, color: "#388e3c" }}>
          {(percentual || 0).toFixed(1)}% do faturamento
        </Typography>
      </Box>
    </Box>
  );
}

function MonitorOsClientesMaisCompram({ filtrosBody }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(defaultData);

  useEffect(() => {
    buscarClientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtrosBody]);

  async function buscarClientes() {
    setLoading(true);
    const result = await ordemServicoService.getRelatorioDashboardClientes(
      filtrosBody
    );
    if (!result.isAxiosError) {
      setData(result.data || defaultData);
    } else {
      responseErros(result);
    }
    setLoading(false);
  }

  const { clientes, totalClientes, totalFaturamento, percentualClientes } =
    data;
  const maxValor = Math.max(...clientes.map((c) => c.valorFaturado || 0), 0);
  const maxOs = Math.max(...clientes.map((c) => c.qtdOs || 0), 0);

  return (
    <Card
      sx={{
        borderRadius: 3,
        border: "1px solid #f0f0f0",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        sx={{
          p: 2,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          "&:last-child": { pb: 2 },
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={1}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            Clientes Que Mais Compram
          </Typography>
          <i
            className="ph-bold ph-crown"
            style={{ color: "#64748b", fontSize: 20 }}
          />
        </Box>
        <Divider sx={{ mb: 2 }} />
        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={40}
                sx={{ borderRadius: 1 }}
              />
            ))}
          </Box>
        ) : clientes.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap={1}
            sx={{ flex: 1 }}
          >
            <i
              className="ph-bold ph-users-three"
              style={{ fontSize: 32, color: "#94a3b8" }}
            />
            <Typography variant="body2" color="text.secondary">
              Nenhuma O.S concluída no período selecionado.
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, overflow: "auto", mb: 2 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f8fafc" }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: 700, width: 44 }}
                      align="center"
                    >
                      #
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 180 }}>
                      Cliente
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 120 }}>
                      OS Concluídas
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, minWidth: 150 }}>
                      Valor Faturado
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700 }}>
                      % Faturamento
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {clientes.map((row, i) => (
                    <TableRow
                      key={row.clienteId || i}
                      sx={{ "&:hover": { backgroundColor: "#f8fafc" } }}
                    >
                      <TableCell align="center">
                        <Box
                          sx={{
                            width: 24,
                            height: 24,
                            borderRadius: "50%",
                            mx: "auto",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: 700,
                            color: i < 3 ? "#fff" : "#475569",
                            backgroundColor: i < 3 ? CORES_RANK[i] : "#e2e8f0",
                          }}
                        >
                          {i + 1}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ color: "#1e293b" }}
                          title={row.clienteNome}
                        >
                          {row.clienteNome}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <BarraProgresso
                          valor={row.qtdOs}
                          max={maxOs}
                          cor="#1976d2"
                          label={row.qtdOs}
                        />
                      </TableCell>
                      <TableCell>
                        <BarraProgresso
                          valor={row.valorFaturado}
                          max={maxValor}
                          cor="#388e3c"
                          label={formatPrice(row.valorFaturado)}
                          labelWidth={82}
                        />
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, color: "#1e293b" }}
                      >
                        {(row.percentual || 0).toFixed(1)}%
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
            <ResumoTotais
              quantidade={clientes.length}
              totalClientes={totalClientes}
              totalFaturamento={totalFaturamento}
              percentual={percentualClientes}
            />
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default MonitorOsClientesMaisCompram;
